import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Pagination,
  Row,
  Spinner,
} from "react-bootstrap";
import { Search } from "react-feather";
import { usePost } from "../../api";
import {
  IOpenDataProductMapQuery,
  IOpenDataProductMapResult,
  IOpenDataProductMatch,
  OpenDataConfidence,
  PRODUCT_MAP_DEFAULT_CONFIDENCE,
} from "../../interfaces";
import { JsonBlock } from "./json-block";
import { ProductLinks } from "./product-links";

/** Upstream ceiling; it also ignores any skip/offset, so paging happens here. */
const MAX_RESULTS = 50;
const PAGE_SIZE = 12;

/**
 * Written for reviewers who should not have to reason about a 0-1 score.
 * Scores are lower without a domain, so each tier names both cases.
 */
const CONFIDENCE_OPTIONS: { value: number; label: string }[] = [
  { value: OpenDataConfidence.Exact, label: "Exact only — manufacturer key or exact SKU" },
  { value: OpenDataConfidence.VeryStrong, label: "Very strong — 3+ words, same domain" },
  { value: OpenDataConfidence.Strong, label: "Strong — 3+ words (or 2 on the domain)" },
  { value: OpenDataConfidence.Balanced, label: "Balanced — 2+ words (or 1 on the domain)" },
  { value: OpenDataConfidence.Any, label: "Any match — includes single-word hits" },
];

const confidenceLabel = (v: number): string =>
  CONFIDENCE_OPTIONS.find((o) => o.value === v)?.label ?? String(v);

/** The nearest tier at or below a score — what the reviewer should switch to. */
const tierFor = (score: number): number =>
  CONFIDENCE_OPTIONS.map((o) => o.value)
    .sort((a, b) => b - a)
    .find((v) => v <= score) ?? OpenDataConfidence.Any;

const confidenceVariant = (c: number): string =>
  c >= OpenDataConfidence.Exact
    ? "success"
    : c >= OpenDataConfidence.VeryStrong
      ? "info"
      : c >= OpenDataConfidence.Balanced
        ? "warning"
        : "secondary";

const price = (m: IOpenDataProductMatch): string => {
  const p = m.product.salePrice ?? m.product.price;
  if (p === null || p === undefined) return "—";
  return `${p.toFixed(2)} ${m.product.currency ?? ""}`.trim();
};

const ProductCard: React.FC<{ match: IOpenDataProductMatch }> = ({ match }) => {
  const { product } = match;

  return (
    <Col md={6} xl={4}>
      <Card className="h-100">
        <div
          className="d-flex align-items-center justify-content-center bg-light border-bottom"
          style={{ height: 160 }}
        >
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
            />
          ) : (
            <span className="text-muted small">No image</span>
          )}
        </div>
        <Card.Body className="small d-flex flex-column">
          <div className="fw-semibold mb-1" title={product.name}>
            {product.name}
          </div>
          <div className="mb-2 d-flex flex-wrap gap-1">
            {match.network && <Badge bg="info">{match.network}</Badge>}
            <Badge bg={confidenceVariant(match.matchConfidence)}>
              {Math.round(match.matchConfidence * 100)}% {match.matchType}
            </Badge>
            {product.inStock === false && <Badge bg="danger">Out of stock</Badge>}
          </div>
          <div className="text-muted mb-1">{match.matchReason}</div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Merchant</span>
            <span className="text-end ms-2">{product.merchantName ?? "—"}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Domain</span>
            <span className="text-end ms-2">{product.domain ?? "—"}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Brand</span>
            <span className="text-end ms-2">{product.manufacturer ?? "—"}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">SKU</span>
            <span className="text-end ms-2 text-break">{product.sku ?? "—"}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Price</span>
            <span className="text-end ms-2 fw-semibold">{price(match)}</span>
          </div>
          <div className="mt-auto pt-2 border-top mt-2">
            <ProductLinks product={product} />
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export const ProductMap: React.FC = () => {
  const [productName, setProductName] = useState("");
  const [domain, setDomain] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [sku, setSku] = useState("");
  const [category, setCategory] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [language, setLanguage] = useState("");
  const [minimumConfidence, setMinimumConfidence] = useState<number>(
    PRODUCT_MAP_DEFAULT_CONFIDENCE
  );
  // Off by default: the search answers "what products does this domain have",
  // and a catalogue that is out of stock today still answers it. On, it answers
  // the narrower "what could a shopper buy right now".
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(1);

  const { data, loading, error, execute } = usePost<
    IOpenDataProductMapResult,
    IOpenDataProductMapQuery
  >();

  const hasCriterion =
    !!productName.trim() ||
    !!sku.trim() ||
    !!manufacturer.trim() ||
    !!category.trim();

  const runSearch = (confidence: number) => {
    if (!hasCriterion) return;
    const body: IOpenDataProductMapQuery = {
      maxResultCount: MAX_RESULTS,
      minimumConfidence: confidence,
    };
    if (productName.trim()) body.productName = productName.trim();
    if (sku.trim()) body.sku = sku.trim();
    if (manufacturer.trim()) body.manufacturer = manufacturer.trim();
    if (category.trim()) body.category = category.trim();
    if (domain.trim()) body.domain = domain.trim();
    if (countryCode.trim()) body.countryCode = countryCode.trim();
    if (language.trim()) body.language = language.trim();
    if (inStockOnly) body.inStockOnly = true;
    setPage(1);
    execute("open-data/product-map", body).catch(() => undefined);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(minimumConfidence);
  };

  const results = data?.results ?? [];
  const pageCount = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const visible = useMemo(
    () => results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [results, page]
  );
  const pageNumbers = useMemo(
    () => Array.from({ length: pageCount }, (_, i) => i + 1),
    [pageCount]
  );

  return (
    <div>
      <div className="mb-3 d-flex align-items-center gap-2 small">
        <Badge bg="primary">Open Data API</Badge>
        <code className="text-muted">POST /v1/OpenData/product-map</code>
      </div>
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={onSubmit}>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Product name</Form.Label>
                  <Form.Control
                    placeholder="e.g. iphone 15 case"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Domain <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="e.g. currys.co.uk"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Brand <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="e.g. Acer"
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    SKU <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="e.g. 10285574"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Category <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="e.g. Laptops"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Country <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="UK"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Language <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="en"
                    maxLength={2}
                    value={language}
                    onChange={(e) => setLanguage(e.target.value.toLowerCase())}
                    style={{ textTransform: "lowercase" }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Match confidence
                  </Form.Label>
                  <Form.Select
                    value={minimumConfidence}
                    onChange={(e) =>
                      setMinimumConfidence(Number(e.target.value))
                    }
                  >
                    {CONFIDENCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Form.Check
                  type="switch"
                  id="product-map-in-stock-only"
                  className="mb-2"
                  label="In stock only"
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                  title="Off returns every match. On returns only items the feed currently reports as in stock."
                />
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-100"
                  disabled={loading || !hasCriterion}
                >
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <Search size={16} className="me-1" /> Search products
                    </>
                  )}
                </Button>
              </Col>
            </Row>
            <div className="small text-muted mt-2">
              Give at least one of product name, SKU, brand or category. Domain
              on its own is not accepted by the API — it narrows the other
              criteria. Country is passed through, but the API does not
              currently filter or rank by it, so treat results as
              all-markets.
            </div>
          </Form>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger">
          {(error.data as any)?.message ??
            "Product search failed. Please try again."}
        </Alert>
      )}

      {data && (
        <>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <Badge bg="dark">{data.count} products</Badge>
            {Object.entries(data.networkCounts).map(([net, n]) => (
              <Badge bg="info" key={net}>
                {net}: {n}
              </Badge>
            ))}
            <Badge bg="light" text="dark">
              min confidence {data.minimumConfidence}
            </Badge>
            {data.truncated && (
              <span className="small text-muted">
                Capped at {MAX_RESULTS} by the API — narrow your search to see
                different matches.
              </span>
            )}
          </div>

          {results.length === 0 ? (
            <Alert variant="warning">
              {data.bestAvailableConfidence !== null ? (
                <>
                  <div>
                    No products at “{confidenceLabel(data.minimumConfidence)}”,
                    but this search does match at{" "}
                    <strong>{data.bestAvailableConfidence}</strong>.
                  </div>
                  <Button
                    size="sm"
                    variant="outline-dark"
                    className="mt-2"
                    onClick={() => {
                      const next = tierFor(
                        data.bestAvailableConfidence as number
                      );
                      setMinimumConfidence(next);
                      runSearch(next);
                    }}
                  >
                    Retry at “
                    {confidenceLabel(tierFor(data.bestAvailableConfidence))}”
                  </Button>
                </>
              ) : (
                "No products matched this search at any confidence level."
              )}
            </Alert>
          ) : (
            <>
              <Row className="g-3">
                {visible.map((m) => (
                  <ProductCard key={m.product.id} match={m} />
                ))}
              </Row>

              {pageCount > 1 && (
                <Pagination className="mt-3 justify-content-center">
                  <Pagination.Prev
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  />
                  {pageNumbers.map((n) => (
                    <Pagination.Item
                      key={n}
                      active={page === n}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    disabled={page === pageCount}
                    onClick={() => setPage((p) => p + 1)}
                  />
                </Pagination>
              )}
            </>
          )}

          <JsonBlock value={data} title="Raw product-map payload" />
        </>
      )}
    </div>
  );
};
